<template>
  <AuthShell
    title="Login"
    subtitle="Access your Filmsomniac account."
    :show-hero="false"
  >
    <div class="auth-panel__section">
      <h2 class="auth-panel__title">Use email to sign in</h2>
      <p class="auth-panel__helper">Continue where you left off.</p>
    </div>

    <div v-if="errorMessage" class="auth-panel__section">
      <div class="auth-error-message" data-testid="login-error">{{ errorMessage }}</div>
    </div>

    <div v-if="successMessage" class="auth-panel__section">
      <div class="auth-success-message" data-testid="login-success">{{ successMessage }}</div>
    </div>

    <form class="auth-panel__section auth-form" @submit.prevent="onSubmit">
      <div class="auth-field">
        <label class="auth-label" for="login-email">Email</label>
        <input
          id="login-email"
          v-model="email"
          class="auth-input"
          data-testid="login-email"
          type="email"
          name="email"
          required
          autofocus
          autocomplete="email"
        >
      </div>

      <div class="auth-field">
        <label class="auth-label" for="login-password">Password</label>
        <input
          id="login-password"
          v-model="password"
          class="auth-input"
          data-testid="login-password"
          type="password"
          name="password"
          required
          autocomplete="current-password"
        >
      </div>

      <div class="auth-checkbox-row">
        <label class="auth-checkbox">
          <input v-model="remember" type="checkbox" name="remember">
          <span>Remember me</span>
        </label>
        <a class="auth-link" href="/forgot-password">Forgot your password?</a>
      </div>

      <div class="auth-actions">
        <button
          class="auth-submit"
          data-testid="login-submit"
          type="submit"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Logging in…' : 'Login' }}
        </button>
        <p class="auth-secondary">
          No account yet?
          <a class="auth-link" href="/sign-up">Register</a>
        </p>
      </div>
    </form>

    <div v-if="showGoogleLogin" class="auth-panel__section auth-divider">
      <span>Or continue with</span>
    </div>

    <div v-if="showGoogleLogin" class="auth-panel__section">
      <div class="auth-social-grid">
        <a class="auth-social-link" :href="googleLoginPath">
          <i class="icon-google" aria-hidden="true"></i>
          <span>Google</span>
        </a>
      </div>
    </div>
  </AuthShell>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AuthShell from '../components/AuthShell.vue'
import { useAuthStore } from '../stores/auth'
import { buildGoogleLoginPath, safeRedirectPath } from '../utils/auth'

const email = ref('')
const password = ref('')
const remember = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isSubmitting = ref(false)
const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const showGoogleLogin = true
const googleLoginPath = buildGoogleLoginPath(route.query.redirect)

onMounted(async () => {
  auth.initFromStorage()
  if (route.query.passwordChanged === '1') {
    successMessage.value = 'Your password was successfully changed. You can now log in with your new password.'
  }
  if (route.query.googleError === 'emailTaken') {
    errorMessage.value = 'An account with that email already exists. Log in with email first, then connect Google from account settings.'
  } else if (route.query.googleError) {
    errorMessage.value = 'Google login failed. Please try again.'
  }
  if (auth.isAuthenticated.value) {
    await router.replace('/home')
  }
})

const resetMessages = () => {
  errorMessage.value = ''
  successMessage.value = ''
}

const onSubmit = async () => {
  resetMessages()
  isSubmitting.value = true
  try {
    const resp = await fetch('/api/v1/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value,
        password: password.value,
        remember: remember.value,
      }),
    })
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}))
      const firstError = typeof data === 'object' ? Object.values(data)[0] : null
      errorMessage.value = Array.isArray(firstError) ? firstError[0] : firstError || 'Login failed.'
      return
    }
    const data = await resp.json()
    auth.setTokens(data.access, data.refresh, data.username)
    const redirectTo = safeRedirectPath(route.query.redirect, '/home')
    await router.push(redirectTo)
  } catch (e) {
    errorMessage.value = 'Network error. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}
</script>
